import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Clock, Copy, Link2, QrCode, X } from 'lucide-react';
import { assessmentApi } from '../../services/assessmentApi';
import { formatDate } from './statusMeta';

const SURVEY_NAME = { initial: 'Initial survey', final: 'Final survey' };

// Has the student done the survey that goes with this assessment? Shown while
// the therapist works so they can remind the student in the session -- or hand
// them a QR code to do it there and then.
export default function SurveyPanel({ uuid, onServer }) {
  const queryClient = useQueryClient();
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, isError, isLoading } = useQuery({
    queryKey: ['assessment-survey', uuid],
    queryFn: () => assessmentApi.surveyStatus(uuid),
    enabled: onServer,
    refetchInterval: 60000,
    retry: 1,
  });

  const link = useMutation({
    mutationFn: () => assessmentApi.surveyLink(uuid),
    onSuccess: (result) => queryClient.setQueryData(['assessment-survey', uuid], result),
  });

  const status = link.data || data;
  const url = status?.link?.url;
  const name = SURVEY_NAME[status?.surveyType] || 'Student survey';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked; the link is visible to copy by hand */ }
  };

  let body;
  if (!onServer || isLoading) {
    body = <p className="text-xs text-slate-500">{onServer ? 'Checking…' : 'Available once this form syncs.'}</p>;
  } else if (isError && !status) {
    body = <p className="text-xs text-slate-500">Can't check right now (offline?).</p>;
  } else if (status.state === 'completed') {
    body = (
      <p className="flex items-center gap-1.5 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        Completed{status.completedAt ? ` ${formatDate(status.completedAt)}` : ''}
      </p>
    );
  } else {
    body = (
      <div className="space-y-2">
        <p className="flex items-center gap-1.5 text-sm text-amber-800">
          <Clock className="h-4 w-4 shrink-0" />
          {status.state === 'link_sent' ? 'Link sent, not completed yet' : 'Not started'}
        </p>
        {url ? (
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={copy}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              <Copy className="h-3.5 w-3.5" /> {copied ? 'Copied' : 'Copy link'}
            </button>
            <button type="button" onClick={() => setShowQr(true)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
              <QrCode className="h-3.5 w-3.5" /> Show QR
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => link.mutate()} disabled={link.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            <Link2 className="h-3.5 w-3.5" /> {link.isPending ? 'Creating…' : 'Get survey link'}
          </button>
        )}
        {link.isError && <p className="text-xs text-red-600">{link.error.message}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{name}</p>
      {body}

      {showQr && url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowQr(false)}>
          <div className="w-full max-w-xs rounded-xl bg-white p-5 text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">{name}</p>
              <button type="button" onClick={() => setShowQr(false)} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <QRCodeSVG value={url} size={220} className="mx-auto" includeMargin />
            <p className="mt-3 text-xs text-slate-500">Have the student scan this with their phone camera.</p>
          </div>
        </div>
      )}
    </div>
  );
}
